import { sql } from 'drizzle-orm';
import { index, integer, sqliteTable, text } from 'drizzle-orm/sqlite-core';
import { syncedColumns } from './common';

/** One taper segment. A mid-way edit appends one that starts that day (core quit/taper.ts). */
export interface TaperSegmentJson {
  from: string;
  startLimit: number;
  step: number;
  everyDays: number;
  floor: number;
}

/** Synced. One row per user: id = singletonId(userId, 'quit_plans'). The quit date is derived. */
export const quitPlans = sqliteTable('quit_plans', {
  ...syncedColumns(),
  substance: text('substance', { enum: ['cigarettes'] })
    .notNull()
    .default('cigarettes'),
  baselinePerDay: integer('baseline_per_day').notNull(),
  /** Local day the plan started; money saved and progress count from here. */
  startDate: text('start_date').notNull(),
  taper: text('taper', { mode: 'json' })
    .$type<TaperSegmentJson[]>()
    .notNull()
    .default(sql`'[]'`),
  /** 'none' = the user chose not to track money. */
  priceKind: text('price_kind', { enum: ['pack', 'stick', 'none'] })
    .notNull()
    .default('none'),
  packPriceMinor: integer('pack_price_minor'),
  perPack: integer('per_pack'),
  stickPriceMinor: integer('stick_price_minor'),
  currency: text('currency', { enum: ['PHP'] })
    .notNull()
    .default('PHP'),
  /** Budget fund the Cigarettes expense deducts from (null = Budget's default rule). */
  fundId: text('fund_id'),
  /** Trigger -> the user's own plan text (defaults live in core). */
  triggerPlans: text('trigger_plans', { mode: 'json' })
    .$type<Record<string, string>>()
    .notNull()
    .default(sql`'{}'`),
  /** ISO 3166 alpha-2 country for the quitline link (null = from the device region). */
  quitlineCountry: text('quitline_country'),
});

/** Synced. A +1 tap or a written day total. The day's count follows spec §7 (core quit/daily.ts). */
export const smokeLogs = sqliteTable(
  'smoke_logs',
  {
    ...syncedColumns(),
    /** When it was logged (UTC). For a backfilled total this is the time of writing. */
    at: text('at').notNull(),
    recordedTz: text('recorded_tz').notNull(),
    /** Local day it counts toward. */
    date: text('date').notNull(),
    kind: text('kind', { enum: ['tap', 'day_total'] }).notNull(),
    /** 1 for a tap; the day's total for day_total. */
    count: integer('count').notNull(),
    /** Cost of this log at the plan price when logged (centavos). */
    costMinor: integer('cost_minor').notNull().default(0),
    currency: text('currency', { enum: ['PHP'] })
      .notNull()
      .default('PHP'),
    trigger: text('trigger'),
  },
  (t) => [index('smoke_logs_user_date_idx').on(t.userId, t.date)],
);

/** Synced. "I had a craving": resisted or not, with an optional trigger and whether the delay timer was used. */
export const cravings = sqliteTable(
  'cravings',
  {
    ...syncedColumns(),
    at: text('at').notNull(),
    recordedTz: text('recorded_tz').notNull(),
    date: text('date').notNull(),
    resisted: integer('resisted', { mode: 'boolean' }).notNull(),
    trigger: text('trigger'),
    delayUsed: integer('delay_used', { mode: 'boolean' }).notNull().default(false),
  },
  (t) => [index('cravings_user_date_idx').on(t.userId, t.date)],
);
